// app/src/components/FlagDetailSlideover.tsx
import React from "react";
import type {
    WorkbenchFlag,
    CostSuggestion,
    FlagEvidenceRow,
} from "../types/workbench";
import { formatCurrency } from "../utils/formatCurrency";
import { usePersistentAsrDraft } from "../hooks/usePersistentAsrDraft";

interface AddServiceDraftFeeLineItem {
    label: string;
    amount: number;
}

interface AddServiceDraft {
    summaryParagraph: string;
    detailedScope: string;
    feeLineItems: AddServiceDraftFeeLineItem[];
    assumptionsAndExclusions: string;
}

type AsyncAction = () => void | Promise<void>;

interface FlagDetailSlideoverProps {
    flag: WorkbenchFlag;
    notesDraft: string;
    onNotesChange: (value: string) => void;
    onSaveNotes: AsyncAction;
    isSavingNotes: boolean;
    onClose: () => void;
    canDismiss: boolean;
    isMutating: boolean;
    onDismiss: AsyncAction;
    onReinstate: AsyncAction;
    costAmountDraft: string;
    onCostAmountChange: (value: string) => void;
    costBasisDraft: string;
    onCostBasisChange: (value: string) => void;
    onSaveCost: AsyncAction;
    isSavingCost: boolean;
    costSuggestion: CostSuggestion | null;
    onAskSuggestion: AsyncAction;
    isLoadingSuggestion: boolean;
    onUseSuggestion: (which: "low" | "mid" | "high") => void; // kept for compatibility, no longer used
    addServiceDraft: AddServiceDraft | null;
    isDraftingService: boolean;
    addServiceError: string | null;
    onDraftAdditionalService: AsyncAction;
    evidence: FlagEvidenceRow[] | null;
    isEvidenceLoading: boolean;
    evidenceError: string | null;
    selectedFlagIds?: string[];

    // 🔽 NEW: ASR draft + export wiring
    asrDraftInitial?: string;
    onExportAsr?: (text: string) => void;
    isExportingAsr?: boolean;
}

const FlagDetailSlideoverComponent: React.FC<FlagDetailSlideoverProps> = ({
    flag,
    notesDraft,
    onNotesChange,
    onSaveNotes,
    isSavingNotes,
    onClose,
    canDismiss,
    isMutating,
    onDismiss,
    onReinstate,
    costAmountDraft,
    onCostAmountChange,
    costBasisDraft,
    onCostBasisChange,
    onSaveCost,
    isSavingCost,
    costSuggestion,
    onAskSuggestion,
    isLoadingSuggestion,
    onUseSuggestion, // unused but retained
    addServiceDraft,
    isDraftingService,
    addServiceError,
    onDraftAdditionalService,
    evidence,
    isEvidenceLoading,
    evidenceError,
    // new props
    asrDraftInitial,
    onExportAsr,
    isExportingAsr,
}) => {
    const createdLabel = flag.createdAt
        ? new Date(flag.createdAt).toLocaleString()
        : "";

    const hasCost = !!flag.costEstimate;

    // 🔽 Persistent ASR draft, keyed by flag.id
    const {
        text: asrDraft,
        setText: setAsrDraft,
        clear: clearAsrDraft,
        loaded: asrDraftLoaded,
    } = usePersistentAsrDraft({
        projectId: flag.projectId,
        flagId: flag.id,
        initialText: asrDraftInitial ?? "",
    });

    // 🔽 Normalize AI cost suggestion into smooth bands
    const normalizedCostSuggestion = React.useMemo<CostSuggestion | null>(() => {
        if (!costSuggestion) return null;

        const center =
            (typeof costSuggestion.mid === "number" && costSuggestion.mid > 0
                ? costSuggestion.mid
                : typeof costSuggestion.high === "number" && costSuggestion.high > 0
                    ? costSuggestion.high
                    : typeof costSuggestion.low === "number" && costSuggestion.low > 0
                        ? costSuggestion.low
                        : 0) || 0;

        if (!center || center <= 0) {
            return costSuggestion;
        }

        const baseLow = Math.round(center * 0.8); // -20%
        const baseMid = Math.round(center * 1.0); // center
        let baseHigh = Math.round(center * 1.4);  // +40%

        const maxHigh = baseLow * 3.5;
        if (baseHigh > maxHigh) {
            baseHigh = Math.round(maxHigh);
        }

        return {
            ...costSuggestion,
            low: baseLow,
            mid: baseMid,
            high: baseHigh,
        };
    }, [costSuggestion]);

    // 🔽 When an Add Service draft arrives, push it into the ASR editor
    React.useEffect(() => {
        if (!addServiceDraft) return;

        const combined = [
            addServiceDraft.summaryParagraph,
            "",
            addServiceDraft.detailedScope,
            "",
            "Fee Table:",
            ...addServiceDraft.feeLineItems.map(
                (item) => `- ${item.label}: ${formatCurrency(item.amount)}`
            ),
            "",
            "Assumptions & Exclusions:",
            addServiceDraft.assumptionsAndExclusions,
        ].join("\n");

        setAsrDraft(combined);
    }, [addServiceDraft, setAsrDraft]);

    const handleExportAsr = () => {
        if (!onExportAsr) return;
        onExportAsr(asrDraft);
        clearAsrDraft();
    };

    // 🔽 Use normalized suggestion ranges and auto-save when one is chosen
    const handleUseSuggestionLocal = (which: "low" | "mid" | "high") => {
        if (!normalizedCostSuggestion) return;
        const value = normalizedCostSuggestion[which];

        if (typeof value !== "number" || !Number.isFinite(value)) return;

        onCostAmountChange(String(value));
        // basis stays as-is; PM can edit if they want more detail
        // Auto-save is handled by the effect below
    };

    // 🔽 Auto-save cost whenever it changes (AI or manual), with debounce
    const hasInitializedCostDraftRef = React.useRef(false);
    const lastAutoSavedRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        // Skip initial mount so we don't auto-save the initial value
        if (!hasInitializedCostDraftRef.current) {
            hasInitializedCostDraftRef.current = true;

            // Initialize the signature to current state so we don't immediately re-save
            const initialAmount = (costAmountDraft ?? "").toString().trim();
            const initialBasis = (costBasisDraft ?? "").toString().trim();
            lastAutoSavedRef.current = `${initialAmount}|${initialBasis}`;

            return;
        }

        const amountTrimmed = (costAmountDraft ?? "").toString().trim();
        const basisTrimmed = (costBasisDraft ?? "").toString().trim();

        if (!amountTrimmed.length) {
            return;
        }

        const signature = `${amountTrimmed}|${basisTrimmed}`;

        // If nothing changed since last auto-save, do nothing
        if (lastAutoSavedRef.current === signature) {
            return;
        }

        if (isSavingCost) {
            // Let the current save finish; we'll pick up next time user edits
            return;
        }

        const handle = setTimeout(() => {
            onSaveCost();
            // Record this as the last auto-saved state
            lastAutoSavedRef.current = signature;
        }, 700);

        return () => clearTimeout(handle);
    }, [costAmountDraft, costBasisDraft, isSavingCost, onSaveCost]);

    return (
        <div className="fixed inset-0 z-40 flex justify-end bg-slate-950/60 backdrop-blur-sm">
            {/* Slideover panel */}
            <div className="flex h-full w-full max-w-3xl flex-col border-l border-slate-800 bg-slate-950/95 shadow-xl">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-800 px-4 py-3">
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                            <h2 className="text-base font-semibold text-slate-50">
                                {flag.title ?? "Untitled flag"}
                            </h2>
                            {flag.status === "dismissed" && (
                                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[11px] text-slate-300">
                                    Dismissed
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                            {flag.classification && (
                                <span className="rounded-md bg-slate-900 px-2 py-0.5 text-[11px] uppercase tracking-wide text-slate-300">
                                    {flag.classification}
                                </span>
                            )}
                            {flag.severity && (
                                <span className="rounded-md bg-slate-900 px-2 py-0.5 text-[11px] uppercase tracking-wide text-slate-300">
                                    Severity: {flag.severity}
                                </span>
                            )}
                            {flag.documentTitle && (
                                <span className="truncate text-xs text-slate-400">
                                    Source: {flag.documentTitle}
                                </span>
                            )}
                            {createdLabel && (
                                <span className="text-xs text-slate-500">
                                    Created {createdLabel}
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-50"
                    >
                        <span className="sr-only">Close</span>
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-5 overflow-auto px-5 py-4">
                    {/* Summary */}
                    {flag.summary && (
                        <section className="space-y-1.5">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                Summary
                            </h3>
                            <p className="text-sm text-slate-200">{flag.summary}</p>
                        </section>
                    )}

                    {/* Justification */}
                    {flag.justificationShort && (
                        <section className="space-y-1.5">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                Justification
                            </h3>
                            <p className="text-sm text-slate-300 leading-relaxed">{flag.justificationShort}</p>
                        </section>
                    )}

                    {/* Evidence (receipts) */}
                    <section className="space-y-2.5">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                Evidence (receipts)
                            </h3>
                            {flag.evidenceCount > 0 && (
                                <span className="rounded-full bg-amber-900/40 px-2.5 py-0.5 text-[11px] font-medium text-amber-300">
                                    {flag.evidenceCount} snippet
                                    {flag.evidenceCount === 1 ? "" : "s"}
                                </span>
                            )}
                        </div>

                        {isEvidenceLoading && (
                            <p className="text-xs text-slate-400">Loading evidence…</p>
                        )}

                        {!isEvidenceLoading && evidenceError && (
                            <p className="text-xs text-red-400">
                                Could not load evidence: {evidenceError}
                            </p>
                        )}

                        {!isEvidenceLoading &&
                            !evidenceError &&
                            (!evidence || evidence.length === 0) && (
                                <p className="text-xs text-slate-500">
                                    No evidence snippets are attached to this flag yet.
                                </p>
                            )}

                        {!isEvidenceLoading &&
                            !evidenceError &&
                            evidence &&
                            evidence.length > 0 && (
                                <div className="space-y-3">
                                    {evidence.map((ev) => (
                                        <div
                                            key={ev.id}
                                            className="rounded-md border border-slate-800/80 bg-slate-950/75 p-3"
                                        >
                                            <div className="mb-1.5 flex items-center justify-between">
                                                <div className="text-xs font-medium text-slate-200">
                                                    {ev.documentTitle ?? "(untitled document)"}
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    {ev.documentType && (
                                                        <span className="rounded-full border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                                                            {ev.documentType}
                                                        </span>
                                                    )}
                                                    {ev.role && (
                                                        <span className="rounded-full bg-slate-900 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                                                            {ev.role}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="whitespace-pre-line text-sm leading-snug text-slate-100">
                                                {ev.snippet}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                    </section>

                    {/* Cost estimate */}
                    <section className="space-y-2.5">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                Fee
                            </h3>
                            {hasCost && flag.costEstimate && (
                                <span className="text-xs text-slate-300">
                                    Current: {formatCurrency(flag.costEstimate.amount)}{" "}
                                    {flag.costEstimate.basis ? `· ${flag.costEstimate.basis}` : ""}
                                </span>
                            )}
                        </div>

                        {/* Amount + Basis inputs */}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_minmax(0,1.3fr)]">
                            <div className="space-y-1.5">
                                <label className="block text-xs text-slate-400">Amount</label>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    value={costAmountDraft}
                                    onChange={(e) => onCostAmountChange(e.target.value)}
                                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-0"
                                    placeholder="e.g. 15000"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="block text-xs text-slate-400">Basis / rationale</label>
                                <input
                                    type="text"
                                    value={costBasisDraft}
                                    onChange={(e) => onCostBasisChange(e.target.value)}
                                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-0"
                                    placeholder="e.g. 40 hrs PM + 60 hrs design + permitting"
                                />
                            </div>
                        </div>

                        {/* Actions + AI */}
                        <div className="space-y-2.5">
                            {/* Save + Ask AI row */}
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={onSaveCost}
                                    disabled={isSavingCost}
                                    className="inline-flex items-center rounded-md bg-amber-500 px-3.5 py-1.5 text-xs font-medium text-slate-900 hover:bg-amber-400 disabled:opacity-60"
                                >
                                    {isSavingCost ? "Saving…" : "Save estimate"}
                                </button>

                                {/*<button
                                    type="button"
                                    onClick={onAskSuggestion}
                                    disabled={isLoadingSuggestion}
                                    className="inline-flex items-center rounded-md bg-sky-600 px-3.5 py-1.5 text-xs font-medium text-slate-50 hover:bg-sky-500 disabled:opacity-60"
                                >
                                    {isLoadingSuggestion ? "Thinking…" : "Ask AI for pricing"}
                                </button>*/}
                            </div>

                            {/* Normalized L / M / H suggestion selectors */}
                            {normalizedCostSuggestion && (
                                <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-300">
                                    <span>Use:</span>
                                    <button
                                        type="button"
                                        onClick={() => handleUseSuggestionLocal("low")}
                                        className="rounded-full bg-slate-800 px-2.5 py-0.5 hover:bg-slate-700"
                                    >
                                        {formatCurrency(normalizedCostSuggestion.low)} L
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleUseSuggestionLocal("mid")}
                                        className="rounded-full bg-slate-800 px-2.5 py-0.5 hover:bg-slate-700"
                                    >
                                        {formatCurrency(normalizedCostSuggestion.mid)} M
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleUseSuggestionLocal("high")}
                                        className="rounded-full bg-slate-800 px-2.5 py-0.5 hover:bg-slate-700"
                                    >
                                        {formatCurrency(normalizedCostSuggestion.high)} H
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>


                    {/* Additional services draft */}
                    <section className="space-y-2.5">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                Additional services draft
                            </h3>
                            <button
                                type="button"
                                onClick={onDraftAdditionalService}
                                disabled={isDraftingService}
                                className="inline-flex items-center rounded-md bg-amber-500 px-3.5 py-1.5 text-xs font-medium text-slate-900 hover:bg-amber-400 disabled:opacity-60"
                            >
                                {isDraftingService ? "Drafting..." : "Draft additional service"}
                            </button>
                        </div>

                        {addServiceError && (
                            <p className="text-xs text-red-400">{addServiceError}</p>
                        )}

                        <p className="text-xs text-slate-500">
                            When you draft from this flag, ScopeShield will generate Add Services
                            language and push it straight into the editor below. You can edit it
                            there and then export.
                        </p>
                    </section>

                    {/* 🔻 ASR draft (persistent) – editable text that will be exported */}
                    <section className="space-y-2.5">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                Add Services Report draft
                            </h3>
                            {!asrDraftLoaded && (
                                <span className="text-[11px] text-slate-500">
                                    Restoring draft…
                                </span>
                            )}
                        </div>
                        <textarea
                            value={asrDraft}
                            onChange={(e) => setAsrDraft(e.target.value)}
                            rows={6}
                            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-50 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-0"
                            placeholder="Refine the Add Services Report language here before exporting…"
                        />
                    </section>

                    {/* Notes – stays at the very bottom of the scroll area */}
                    <section className="space-y-2.5">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                Notes
                            </h3>
                        </div>
                        <textarea
                            value={notesDraft}
                            onChange={(e) => onNotesChange(e.target.value)}
                            rows={4}
                            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-50 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-0"
                            placeholder="Add internal notes about this flag – client conversations, decisions, assumptions, etc."
                        />
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={onSaveNotes}
                                disabled={isSavingNotes}
                                className="inline-flex items-center rounded-md bg-slate-100 px-3.5 py-1.5 text-xs font-medium text-slate-900 hover:bg-white disabled:opacity-60"
                            >
                                {isSavingNotes ? "Saving…" : "Save notes"}
                            </button>
                        </div>
                    </section>
                </div>

                {/* Footer actions */}
                <div className="border-t border-slate-800 px-5 py-3.5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-xs text-slate-500">
                            Status:{" "}
                            <span className="font-medium text-slate-200">
                                {flag.status === "dismissed" ? "Dismissed" : "Open"}
                            </span>
                        </div>

                        <div className="flex items-center gap-2.5">
                            {/* Reinstate / Dismiss */}
                            {flag.status === "dismissed" ? (
                                <button
                                    type="button"
                                    onClick={onReinstate}
                                    disabled={isMutating}
                                    className="inline-flex items-center rounded-md border border-slate-700 bg-slate-900 px-3.5 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-800 disabled:opacity-60"
                                >
                                    {isMutating ? "Working…" : "Reinstate flag"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onDismiss}
                                    disabled={!canDismiss || isMutating}
                                    className="inline-flex items-center rounded-md border border-rose-700 bg-rose-950 px-3.5 py-1.5 text-xs font-medium text-rose-100 hover:bg-rose-900 disabled:opacity-60"
                                >
                                    {isMutating ? "Working…" : "Dismiss flag"}
                                </button>
                            )}

                            {/* 🔻 Copy-to-clipboard ASR button */}
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(asrDraft);
                                        console.log("ASR copied to clipboard");
                                    } catch (err) {
                                        console.error("Failed to copy ASR:", err);
                                    }
                                }}
                                disabled={!asrDraft.trim().length}
                                className="inline-flex items-center rounded-md bg-amber-500 px-3.5 py-1.5 text-xs font-medium text-slate-900 hover:bg-amber-400 disabled:opacity-60"
                            >
                                Copy ASR text
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export const FlagDetailSlideover = FlagDetailSlideoverComponent;
